import axios from 'axios';
import { logScreenerUploadError } from '../utils/statsig';
import { getDeviceType } from '../utils/getDeviceType';
import { getWithExpiry } from '../utils/sessionStorage';

const BASE_URL = '/api/v1/media';

/**
 * Media upload response type
 */
export interface MediaUploadResponse {
  videoUrl: string;
  audioUrl: string;
}

/**
 * Generate fallback mock URLs
 */
const generateMockUrls = (fileName: string): MediaUploadResponse => {
  const timestamp = Date.now();
  const fileBase = (fileName && typeof fileName === 'string') 
    ? fileName.split('.')[0] 
    : `file_${timestamp}`;
  
  return {
    videoUrl: `https://mock-s3-bucket.amazonaws.com/videos/${fileBase}_${timestamp}.mp4`,
    audioUrl: `https://mock-s3-bucket.amazonaws.com/audio/${fileBase}_${timestamp}.mp3`
  };
};

export const MediaService = {
  /**
   * Upload a media file (audio or video) and save to S3
   * @param file The file to upload
   * @param isVideo Whether the file is a video
   */
  uploadMedia: async (file: File, isVideo: boolean = true): Promise<MediaUploadResponse> => {
    const formData = new FormData();
    formData.append('media', file);
    formData.append('isVideo', isVideo.toString());
    
    try {
      const response = await axios.post(`${BASE_URL}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const data = response.data;
      const timestamp = Date.now();
      const fileBase = file && file.name ? file.name.split('.')[0] : `file_${timestamp}`;
            
      // Handle the video+audio response structure
      if (data.data?.video) {
        const videoUrl = data.data.video.url;
        
        // If audio extraction failed, use mock audio URL
        if (!data.data.audio?.url) {
          console.warn('Audio extraction failed, using mock audio URL');
          return {
            videoUrl,
            audioUrl: `https://mock-s3-bucket.amazonaws.com/audio/${fileBase}_${timestamp}.mp3`
          };
        }
        
        return {
          videoUrl,
          audioUrl: data.data.audio.url
        };
      } 
      // Handle audio-only files
      else if (data.data?.audio) {
        const audioUrl = data.data.audio.url;
        return {
          videoUrl: ``,
          audioUrl
        };
      }
      // Handle other response formats
      else if (data.data?.url) {
        // Legacy or simple response format
        if (!isVideo) {
          return {
            videoUrl: ``,
            audioUrl: data.data.url
          };
        } else {
          return {
            videoUrl: data.data.url,
            audioUrl: `https://mock-s3-bucket.amazonaws.com/audio/${fileBase}_${timestamp}.mp3`
          };
        }
      }
      // Fallback
      else {
        return generateMockUrls(file?.name);
      }
    } catch (error: unknown) {
      console.error('Media upload failed:', error);

      const uploadError = error as { name?: string; message?: string } | null;
      logScreenerUploadError({
        jobId: getWithExpiry('jobId') || '',
        companyId: getWithExpiry('companyId') || '',
        applicantGuid: getWithExpiry('applicantGuid') || undefined,
        deviceType: getDeviceType(),
        errorCode: uploadError?.name || 'MediaUploadError',
        errorMessage: uploadError?.message || 'Media upload failed via MediaService',
      });

      return generateMockUrls(file?.name);
    }
  },

  /**
   * Transcribe an audio file
   */
  transcribeAudio: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('audio', file);
    
    try {
      const response = await axios.post(`${BASE_URL}/audio/transcribe`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('Audio transcription failed:', error);
      throw new Error('Failed to transcribe audio');
    }
  },

  /**
   * Generate TTS audio
   */
  generateTTSAudio: async (text: string, appGuid: string): Promise<any> => {
    const response = await axios.post(`${BASE_URL}/audio/tts`, { text, appGuid });
    return response.data;
  },

  /**
   * Extract audio from a video file
   */
  extractAudio: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('video', file);
    
    try {
      const response = await axios.post(`${BASE_URL}/video/extract-audio`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error) {
      console.error('Audio extraction failed:', error);
      throw new Error('Failed to extract audio from video');
    }
  },

    /**
     * Download single media file (optimized with redirect)
     */
    downloadMedia: async (url: string, type: 'video' | 'audio', questionIndex: number, applicantName: string): Promise<void> => {
      const format = type === 'video' ? 'mp4' : 'mp3';
      const filename = `question-${questionIndex + 1}-${applicantName.replace(/\s+/g, '-')}.${format}`;
      
      try {
        const downloadUrl = `${BASE_URL}/download-media?url=${encodeURIComponent(url)}&type=${type}&format=${format}`;
        
        console.log(`Starting download for: ${filename}`);
        
        const response = await axios.get(downloadUrl, {
          responseType: 'blob', // Important for file downloads
          timeout: 300000, // 5 minutes timeout for large files
        });
    
        // Create blob from response
        const blob = new Blob([response.data], {
          type: type === 'video' ? 'video/mp4' : 'audio/mpeg'
        });
    
        // Create download link
        const downloadLink = document.createElement('a');
        downloadLink.href = URL.createObjectURL(blob);
        downloadLink.download = filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    
        // Clean up blob URL
        URL.revokeObjectURL(downloadLink.href);
        
        console.log(`Successfully downloaded: ${filename}`);
      } catch (error: any) {
        console.error('Download failed:', error);
        
        // Provide user-friendly error messages
        if (error.code === 'ECONNABORTED') {
          throw new Error('Download timeout - file may be too large');
        } else if (error.response?.status === 404) {
          throw new Error('Media file not found');
        } else if (error.response?.status === 500) {
          throw new Error('Server error during conversion');
        } else {
          throw new Error('Failed to download media file');
        }
      }
    },

  /**
   * Download media file and return as blob for zipping (uses proxy endpoint)
   */
  downloadMediaAsBlob: async (url: string, type: 'video' | 'audio', questionIndex: number, applicantName: string): Promise<{ blob: Blob, filename: string } | null> => {
    const format = type === 'video' ? 'mp4' : 'mp3';
    const filename = `question-${questionIndex + 1}-${applicantName.replace(/\s+/g, '-')}.${format}`;
    
    try {
      const downloadUrl = `${BASE_URL}/download-media-blob?url=${encodeURIComponent(url)}&type=${type}&format=${format}`;
      
      // ✅ Pre-validate endpoint availability
      console.log(`Validating blob download for: ${filename}`);
      const testResponse = await fetch(downloadUrl, { 
        method: 'HEAD',
        // timeout: 10000 // 10 second timeout for validation
      });
      
      if (!testResponse.ok) {
        throw new Error(`Server validation failed: ${testResponse.status} ${testResponse.statusText}`);
      }
      
      // ✅ Validate Content-Type header if available
      const contentType = testResponse.headers.get('Content-Type');
      if (contentType) {
        const expectedType = type === 'video' ? 'video/' : 'audio/';
        if (!contentType.startsWith(expectedType)) {
          console.warn(`Unexpected content type: ${contentType} for ${type} file`);
        }
      }
      
      // ✅ Check Content-Length to avoid downloading empty files
      const contentLength = testResponse.headers.get('Content-Length');
      if (contentLength && parseInt(contentLength) === 0) {
        throw new Error('File appears to be empty');
      }
      
      console.log(`Validation passed, downloading blob for: ${filename}`);
      
      // ✅ Download with improved error handling
      const response = await axios.get(downloadUrl, {
        responseType: 'blob',
        timeout: 300000, // 5 minutes timeout
        validateStatus: (status) => status === 200, // Only accept 200 OK
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            if (percentCompleted % 25 === 0) { // Log every 25%
              console.log(`Download progress for ${filename}: ${percentCompleted}%`);
            }
          }
        }
      });
  
      // ✅ Validate response data
      if (!response.data || response.data.size === 0) {
        throw new Error('Received empty file data');
      }
  
      // ✅ Validate blob size (minimum reasonable file size)
      const minFileSize = 1024; // 1KB minimum
      if (response.data.size < minFileSize) {
        throw new Error(`File too small (${response.data.size} bytes), may be corrupted`);
      }
  
      // ✅ Create blob with proper MIME type validation
      const expectedMimeType = type === 'video' ? 'video/mp4' : 'audio/mpeg';
      const blob = new Blob([response.data], {
        type: expectedMimeType
      });
  
      console.log(`Successfully downloaded blob: ${filename} (${blob.size} bytes)`);
      
      return { blob, filename };
  
    } catch (error: any) {
      console.error(`Failed to download ${filename}:`, error);
      
      // ✅ Provide specific error handling based on error type
      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNABORTED') {
          console.error(`Download timeout for ${filename} after 5 minutes`);
          return null; // Timeout - don't break the entire ZIP process
        }
        
        if (error.response?.status === 404) {
          console.error(`File not found: ${filename}`);
          return null; // File not found - continue with other files
        }
        
        if (error.response?.status === 500) {
          console.error(`Server error processing ${filename}`);
          return null; // Server error - continue with other files
        }
        
        if (error.response?.status && error.response?.status >= 400) {
          console.error(`HTTP ${error.response?.status} error for ${filename}: ${error.response?.statusText}`);
          return null;
        }
      }
      
      // ✅ Handle network and validation errors
      if (error.message.includes('validation failed')) {
        console.error(`Pre-validation failed for ${filename}: ${error.message}`);
        return null;
      }
      
      if (error.message.includes('empty') || error.message.includes('too small')) {
        console.error(`Invalid file content for ${filename}: ${error.message}`);
        return null;
      }
      
      // ✅ Generic network error
      console.error(`Network error downloading ${filename}: ${error.message}`);
      return null; // Return null to continue ZIP process with other files
    }
  },

};